import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Store } from "lucide-react";
import DetailDrawer from "../DetailDrawer";
import {
  getAdminOutletRadiusById,
  updateAdminOutletRadiusById,
} from "../../../services/api";
import "./SessionsView.css";

const DEFAULT_RADIUS_METERS = 500;

const normalizeRadiusInput = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isInteger(parsed)) return null;
  return parsed;
};

export default function OutletRadiusDrawer({
  isOpen,
  outlet,
  onClose,
  onSaved,
  t,
  showError,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [radiusInput, setRadiusInput] = useState("");
  const [loadedRadius, setLoadedRadius] = useState(null);
  const [details, setDetails] = useState(null);
  const [validationError, setValidationError] = useState("");
  const saveInFlightRef = useRef(false);

  const outletId = useMemo(
    () => Number.parseInt(outlet?.outlet_id || outlet?.id, 10) || null,
    [outlet],
  );

  useEffect(() => {
    if (!isOpen || !outletId) {
      setLoading(false);
      setDetails(null);
      setLoadedRadius(null);
      setRadiusInput("");
      setValidationError("");
      return;
    }
    let cancelled = false;

    const load = async () => {
      try {
        // Clear stale outlet content first so the skeleton shows while the next outlet loads.
        setLoading(true);
        setDetails(null);
        setLoadedRadius(null);
        setRadiusInput("");
        setValidationError("");
        const response = await getAdminOutletRadiusById(outletId);
        if (cancelled) return;

        const payload = response?.data?.data || response?.data || response || {};
        const defaultRadius = Number.parseInt(payload.default_radius_meters, 10);
        const normalizedDefaultRadius =
          Number.isInteger(defaultRadius) && defaultRadius > 0
            ? defaultRadius
            : DEFAULT_RADIUS_METERS;
        const configured = Number.parseInt(payload.configured_radius_meters, 10);
        const normalizedConfigured =
          Number.isInteger(configured) && configured !== normalizedDefaultRadius
            ? configured
            : null;

        setDetails({
          ...payload,
          default_radius_meters: normalizedDefaultRadius,
          configured_radius_meters: normalizedConfigured,
          effective_radius_meters:
            Number.isInteger(Number.parseInt(payload.effective_radius_meters, 10))
              ? Number.parseInt(payload.effective_radius_meters, 10)
              : normalizedConfigured ?? normalizedDefaultRadius,
        });
        setLoadedRadius(normalizedConfigured);
        setRadiusInput("");
      } catch (error) {
        if (!cancelled) {
          setDetails(null);
          showError(
            error?.response?.data?.error ||
              t("admin.sessions.outletRadius.failedToLoad"),
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [isOpen, outletId, showError, t]);

  const handleSave = async () => {
    if (saving || saveInFlightRef.current) {
      return;
    }

    const normalizedRadius = normalizeRadiusInput(radiusInput);

    if (radiusInput.trim() && normalizedRadius === null) {
      setValidationError(t("admin.sessions.outletRadius.invalidNumber"));
      return;
    }

    if (normalizedRadius !== null && loadedRadius !== null && normalizedRadius === loadedRadius) {
      setValidationError(t("admin.sessions.outletRadius.sameAsConfigured"));
      return;
    }

    if (
      normalizedRadius !== null &&
      (normalizedRadius < 10 || normalizedRadius > 10000)
    ) {
      setValidationError(t("admin.sessions.outletRadius.invalidRange"));
      return;
    }

    try {
      saveInFlightRef.current = true;
      setSaving(true);
      setValidationError("");

      const response = await updateAdminOutletRadiusById(outletId, {
        allowed_radius_meters: normalizedRadius,
      });

      const payload = response?.data?.data || response?.data || response || {};
      const defaultRadius = Number.parseInt(payload.default_radius_meters, 10);
      const normalizedDefaultRadius =
        Number.isInteger(defaultRadius) && defaultRadius > 0
          ? defaultRadius
          : DEFAULT_RADIUS_METERS;
      const configured = Number.parseInt(payload.configured_radius_meters, 10);
      const normalizedConfigured =
        Number.isInteger(configured) && configured !== normalizedDefaultRadius
          ? configured
          : null;

      setLoadedRadius(normalizedConfigured);
      setDetails({
        ...payload,
        default_radius_meters: normalizedDefaultRadius,
        configured_radius_meters: normalizedConfigured,
        effective_radius_meters:
          Number.isInteger(Number.parseInt(payload.effective_radius_meters, 10))
            ? Number.parseInt(payload.effective_radius_meters, 10)
            : normalizedConfigured ?? normalizedDefaultRadius,
      });
      setRadiusInput("");

      if (typeof onSaved === "function") {
        onSaved();
      }
    } catch (error) {
      setValidationError(
        error?.response?.data?.error || t("admin.sessions.outletRadius.saveFailed"),
      );
    } finally {
      saveInFlightRef.current = false;
      setSaving(false);
    }
  };

  const isDirty = (loadedRadius == null ? "" : String(loadedRadius)) !== radiusInput.trim();
  const sameAsConfiguredError =
    radiusInput.trim() &&
    loadedRadius !== null &&
    normalizeRadiusInput(radiusInput) === loadedRadius
      ? t("admin.sessions.outletRadius.sameAsConfigured")
      : "";
  const activeValidationError = validationError || sameAsConfiguredError;
  const defaultRadiusText =
    details?.default_radius_meters != null
      ? `${details.default_radius_meters} m`
      : `${DEFAULT_RADIUS_METERS} m`;
  const outletRadiusText =
    details?.effective_radius_meters != null
      ? `${details.effective_radius_meters} m`
      : defaultRadiusText;
  const appliedRadiusText =
    details?.radius_source === "outlet" ? outletRadiusText : defaultRadiusText;
  const outletName =
    details?.name ||
    outlet?.outlet_name ||
    outlet?.name ||
    t("admin.sessions.na");
  const outletAddress = details?.address || outlet?.address || "";

  if (loading && !details) {
    return (
      <DetailDrawer
        isOpen={isOpen}
        onClose={onClose}
        title={t("admin.sessions.outletRadius.drawerTitle")}
      >
        <div className="admin-outlet-radius-drawer">
          <section className="admin-outlet-radius-summary is-loading">
            <div className="skeleton-box admin-outlet-radius-skeleton-pill" />
            <div className="skeleton-box admin-outlet-radius-skeleton-title" />
            <div className="skeleton-box admin-outlet-radius-skeleton-subtitle" />
          </section>

          <section className="admin-session-audit-section">
            <div className="admin-session-audit-stack">
              <div className="admin-session-audit-row is-loading">
                <span className="skeleton-box admin-outlet-radius-skeleton-label" />
                <strong className="skeleton-box admin-outlet-radius-skeleton-value" />
              </div>
              <div className="admin-session-audit-row is-loading">
                <span className="skeleton-box admin-outlet-radius-skeleton-label" />
                <strong className="skeleton-box admin-outlet-radius-skeleton-value" />
              </div>
              <div className="admin-session-audit-row is-loading">
                <span className="skeleton-box admin-outlet-radius-skeleton-label" />
                <strong className="skeleton-box admin-outlet-radius-skeleton-value" />
              </div>
            </div>
          </section>

          <section className="admin-session-audit-section">
            <h5>{t("admin.sessions.outletRadius.section.edit")}</h5>
            <div className="admin-outlet-radius-form">
              <div className="skeleton-box admin-outlet-radius-skeleton-input" />
              <div className="skeleton-box admin-outlet-radius-skeleton-hint" />
              <div className="admin-outlet-radius-actions is-loading">
                <div className="skeleton-box admin-outlet-radius-skeleton-button" />
              </div>
            </div>
          </section>
        </div>
      </DetailDrawer>
    );
  }

  return (
    <DetailDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={t("admin.sessions.outletRadius.drawerTitle")}
    >
      <div className="admin-outlet-radius-drawer">
        {saving ? (
          <div className="admin-outlet-radius-saving-overlay" aria-busy="true" aria-live="polite">
            <div className="admin-outlet-radius-saving-card">
              <Loader2 size={24} className="is-spinning" />
              <strong>{t("admin.common.saving")}</strong>
              <span>{t("admin.sessions.outletRadius.saving")}</span>
            </div>
          </div>
        ) : null}

        <section className="admin-outlet-radius-summary">
          <div className="admin-outlet-radius-summary-icon" aria-hidden="true">
            <Store size={18} />
          </div>
          <div className="admin-outlet-radius-summary-copy">
            <strong>
              {outletName}
            </strong>
            {outletAddress ? <span>{outletAddress}</span> : null}
          </div>
        </section>

        <section className="admin-session-audit-section admin-outlet-radius-current-section">
          <div className="admin-session-audit-stack">
            <div className="admin-session-audit-row admin-outlet-radius-current-row">
              <span>{t("admin.sessions.outletRadius.originalRadius")}</span>
              <strong>{defaultRadiusText}</strong>
            </div>
            <div className="admin-session-audit-row admin-outlet-radius-current-row">
              <span>{t("admin.sessions.outletRadius.configuredRadius")}</span>
              <strong>{outletRadiusText}</strong>
            </div>
            <div className="admin-session-audit-row is-subtle admin-outlet-radius-current-row">
              <span>{t("admin.sessions.outletRadius.radiusSource")}</span>
              <strong>{appliedRadiusText}</strong>
            </div>
          </div>
        </section>

        <section className="admin-session-audit-section admin-outlet-radius-edit-section">
          <h5>{t("admin.sessions.outletRadius.section.edit")}</h5>
          <div className="admin-outlet-radius-form">
            <label htmlFor="adminOutletRadiusInput">
              {t("admin.sessions.outletRadius.inputLabel")}
            </label>
            <input
              id="adminOutletRadiusInput"
              type="number"
              min={10}
              max={10000}
              step={1}
              value={radiusInput}
              onChange={(event) => setRadiusInput(event.target.value)}
              placeholder={t("admin.sessions.outletRadius.inputPlaceholder")}
              className="admin-sessions-input"
              disabled={loading || saving}
            />
            <p className="admin-outlet-radius-hint">
              {t("admin.sessions.outletRadius.helper")}
            </p>
            {activeValidationError ? (
              <p className="admin-outlet-radius-error">{activeValidationError}</p>
            ) : null}
          </div>
        </section>

        <div className="admin-outlet-radius-actions">
          <button
            type="button"
            className="admin-btn-primary admin-outlet-radius-save-btn"
            onClick={handleSave}
            disabled={loading || saving || !isDirty}
          >
            {saving ? (
              <>
                <Loader2 size={14} className="admin-outlet-radius-save-icon is-spinning" />
                {t("admin.common.saving")}
              </>
            ) : (
              t("admin.common.saveChanges")
            )}
          </button>
        </div>
      </div>
    </DetailDrawer>
  );
}
